import { FC, useEffect, useMemo, useRef, useState } from "react";
import Row from "../../../common/components/grid/row";
import Column from "../../../common/components/grid/column";
import MapComponent from "../../../common/components/map/map.component";
import MessengerComponent from "../../../common/components/messenger/messenger.component";
import MapRoute from "../../../common/components/map/types/map-route";
import RouteDetailsLoaderComponent from "./common/components/loader/route-details-loader.component";
import RouteDetailsErrorComponent from "./common/components/error/route-details-error.component";
import RouteDetailsToolsComponent from "./common/components/tools/route-details-tools.component";
import PlanEntryDetailsPassengersComponent from "./common/components/passengers/route-details-passengers.component";
import routeDetailsHelper from "./common/route-details.helper";
import RouteDetailsDriverComponent from "./common/components/driver/route-details-driver.component";
import routeDetailsFactory from "./common/route-details.factory";
import RouteDetailsRouteWaypoint from "./common/types/route-details-route-waypoint";
import useAbort from "../../../common/hooks/use-abort";
import MapMarker from "../../../common/components/map/types/map-marker";
import mapMarkerIconFactory from "../../../common/components/map/marker/map-marker-icon.factory";
import usePlanEntryRoadRoutes from "../../../common/services/plan-entry/road-route/use-plan-entry-route-road";
import PlanEntryRoadRouteParams from "../../../common/services/plan-entry/road-route/plan-entry-road-route-params";
import routeRoadRouteLoadParamsFactory from "./common/route-road-route-load-params.factory";
import RouteDetailsRouteComponent from "./common/components/route/route-details-route.component";
import RouteDetailsRouteWaypointGroup from "./common/types/route-details-route-waypoint-group";
import dateService from "../../../common/utils/date/date.service";
import useGpsPlanEntryRecords from "../../../common/services/gps/plan-entry-records/use-gps-plan-entry-records";
import { GpsPlanEntryRecordsItem } from "../../../common/services/gps/plan-entry-records/gps-plan-entry-records";
import usePlanEntryDetails from "../../../common/services/plan-entry/details/use-plan-entry-details";
import { PlanEntryDetailsPassenger } from "../../../common/services/plan-entry/details/plan-entry-details";

type RouteDetailsProps = {
  routeUuid: string;
  routeHumanId: number;
  isVisible: boolean;
  isOrderFinished?: boolean;
};

const RouteDetailsComponent: FC<RouteDetailsProps> = (props) => {
  const leftColumnContentRef = useRef<HTMLDivElement>(null);
  const rightColumnContentHeight = useMemo(() => {
    return leftColumnContentRef.current?.offsetHeight || 500;
  }, [leftColumnContentRef.current?.offsetHeight]);

  const planEntryDetails = usePlanEntryDetails();
  const planEntryDetailsAbort = useAbort();

  const planEntryRecords = useGpsPlanEntryRecords();
  const planEntryRecordsAbort = useAbort();

  const roadRoutes = usePlanEntryRoadRoutes();

  const [shouldShowCompletedMapRoutes, setShouldShowCompletedMapRoutes] =
    useState(false);

  const [selectedPassenger, setSelectedPassenger] =
    useState<PlanEntryDetailsPassenger | null>(null);

  const [hoveredMarker, setHoveredMarker] = useState<MapMarker | null>(null);

  const waypointGroups: RouteDetailsRouteWaypointGroup[] = useMemo(() => {
    if (!planEntryDetails.data) {
      return [];
    }

    return routeDetailsFactory.createWaypointGroups(
      planEntryDetails.data,
      roadRoutes.data,
    );
  }, [planEntryDetails.data, roadRoutes.data]);

  const totalWaypoints: RouteDetailsRouteWaypoint[] = useMemo(() => {
    return waypointGroups.map((group) => group.waypoints).flat();
  }, [waypointGroups]);

  const completedMapRoute: MapRoute | undefined = useMemo(() => {
    if (!shouldShowCompletedMapRoutes || !planEntryRecords.data) {
      return;
    }

    return routeDetailsFactory.createCompletedMapRoute(
      planEntryRecords.data.data,
    );
  }, [shouldShowCompletedMapRoutes, planEntryRecords.data]);

  useEffect(() => {
    if (!props.isVisible) {
      return;
    }

    loadDetails();
  }, [props.isVisible]);

  useEffect(() => {
    if (
      !props.isVisible ||
      !totalWaypoints.length ||
      !shouldShowCompletedMapRoutes
    ) {
      return;
    }

    loadCompletedRoute();
  }, [shouldShowCompletedMapRoutes]);

  useEffect(() => {
    if (!props.isVisible || !totalWaypoints.length) {
      return;
    }

    loadRoadRoutes();
  }, [props.isVisible, totalWaypoints.length]);

  const loadDetails = () => {
    planEntryDetails.load(
      { planEntryUuid: props.routeUuid },
      planEntryDetailsAbort.signal,
    );
  };

  const loadCompletedRoute = () => {
    planEntryRecords.load(
      { planEntryUuid: props.routeUuid },
      planEntryRecordsAbort.signal,
    );
  };

  const loadRoadRoutes = () => {
    const params: PlanEntryRoadRouteParams[] = waypointGroups.map((group) =>
      routeRoadRouteLoadParamsFactory.create(group),
    );

    roadRoutes.load(params);
  };

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers = routeDetailsFactory
      .createMapMarkers(totalWaypoints, selectedPassenger?.uuid)
      .map((marker) => ({ ...marker, isRouteMarker: true }));

    return mapMarkers;
  }, [selectedPassenger?.uuid, totalWaypoints]);

  const onMarkerHover = (marker: MapMarker | null) => {
    setHoveredMarker(marker);
  };

  const generateTimeBasedMarkers = (
    completedRoute: GpsPlanEntryRecordsItem[],
    existingRouteMarkers: MapMarker[],
  ): MapMarker[] => {
    if (!completedRoute || completedRoute.length === 0) {
      return [];
    }

    const markers: MapMarker[] = [];

    let startTime = completedRoute[0].recordTime.getTime();
    let nextMarkerTime = startTime;

    let index = 0;

    completedRoute.forEach((currPoint) => {
      let currTime = currPoint.recordTime.getTime();

      if (currTime >= nextMarkerTime) {
        nextMarkerTime = currTime + 60000;
        const date = new Date(currPoint.recordTime);

        if (dateService.checkIsValidDate(date)) {
          const formattedTime = dateService.formatTime(date);

          const newMarker = {
            coordinate: {
              latitude: currPoint.latitude,
              longitude: currPoint.longitude,
            },
            icon: mapMarkerIconFactory.createGpsIcon(),
            title: formattedTime,
            isGpsRouteMarker: true,
            onMouseOver: () =>
              onMarkerHover({
                coordinate: {
                  latitude: currPoint.latitude,
                  longitude: currPoint.longitude,
                },
                icon: mapMarkerIconFactory.createGpsIcon(),
                title: formattedTime,
              }),
            onMouseOut: () => onMarkerHover(null),
            index: index,
          };

          index++;

          const isDuplicate = existingRouteMarkers.some(
            (routeMarker) =>
              routeMarker.coordinate.latitude ===
                newMarker.coordinate.latitude &&
              routeMarker.coordinate.longitude ===
                newMarker.coordinate.longitude &&
              routeMarker.isGpsRouteMarker,
          );

          if (!isDuplicate) {
            markers.push(newMarker);
          }
        }
      }
    });

    return markers;
  };

  const mapRoutes: MapRoute[] = useMemo(() => {
    const routes: MapRoute[] = routeDetailsFactory.createMapRoutes(
      roadRoutes.data.flat(),
    );

    if (shouldShowCompletedMapRoutes && completedMapRoute) {
      routes.push(completedMapRoute);
    }

    return routes;
  }, [roadRoutes.data, completedMapRoute, shouldShowCompletedMapRoutes]);

  const memoizedMarkers = useMemo(() => {
    if (!completedMapRoute) {
      return (
        <MapComponent
          key={
            hoveredMarker?.coordinate.latitude +
            "-" +
            hoveredMarker?.coordinate.longitude
          }
          markers={mapMarkers}
          autoFit
          routes={mapRoutes}
        />
      );
    }

    if (!planEntryRecords.data) {
      return null;
    }

    const gpsMarkers: MapMarker[] = generateTimeBasedMarkers(
      planEntryRecords.data.data,
      mapMarkers,
    );

    return (
      <MapComponent
        key={
          hoveredMarker?.coordinate.latitude +
          "-" +
          hoveredMarker?.coordinate.longitude
        }
        markers={mapMarkers}
        gpsMarkers={gpsMarkers}
        autoFit
        routes={mapRoutes}
        shouldShowCompletedMapRoutes={shouldShowCompletedMapRoutes}
      />
    );
  }, [shouldShowCompletedMapRoutes, planEntryRecords, mapMarkers, mapRoutes]);

  const onPassengerRowClick = (passenger: PlanEntryDetailsPassenger) => {
    if (passenger.uuid === selectedPassenger?.uuid) {
      setSelectedPassenger(null);
      return;
    }

    setSelectedPassenger(passenger);
  };

  const toggleCompletedMapRoutesActive = () => {
    setShouldShowCompletedMapRoutes((curr) => !curr);
  };

  const isCompletedRoutesFetching = planEntryRecords.isLoading;

  const isCompletedRouteButtonActive = shouldShowCompletedMapRoutes;

  const routeDetailsToolsProps = {
    isCompletedRoutesFetching,
    isCompletedRouteButtonActive,
    toggleCompletedMapRoutesActive,
  };

  const messengerChannelsAvailability =
    routeDetailsHelper.getMessengerChannelAvailability();

  const DetailsContent = (
    <Row>
      <Column lg={7}>
        <div ref={leftColumnContentRef}>
          <Row>
            <Column>
              <div className="route_details_map_wrapper">
                {memoizedMarkers}
                <RouteDetailsToolsComponent {...routeDetailsToolsProps} />
              </div>
            </Column>
          </Row>
          <Row>
            <Column md={4} lg={3}>
              {!!planEntryDetails.data?.passengers.length && (
                <PlanEntryDetailsPassengersComponent
                  onPassengerRowClick={onPassengerRowClick}
                  passengers={planEntryDetails.data.passengers}
                  selectedPassenger={selectedPassenger}
                  waypoints={totalWaypoints}
                />
              )}
              {!!planEntryDetails.data?.driver && (
                <RouteDetailsDriverComponent
                  driver={planEntryDetails.data.driver}
                  planEntryUuid={
                    props.isOrderFinished ? planEntryDetails.data.id : undefined
                  }
                />
              )}
            </Column>
            <Column md={8} lg={9}>
              {!!totalWaypoints.length && (
                <RouteDetailsRouteComponent
                  waypoints={totalWaypoints}
                  routeUuid={props.routeUuid}
                  selectedPassenger={selectedPassenger}
                />
              )}
            </Column>
          </Row>
        </div>
      </Column>
      <Column lg={5}>
        <div style={{ height: rightColumnContentHeight }}>
          <MessengerComponent
            channelsAvailability={messengerChannelsAvailability}
            planEntryUuid={props.routeUuid}
          />
        </div>
      </Column>
    </Row>
  );

  const ErrorContent = (
    <RouteDetailsErrorComponent
      routeHumanId={props.routeHumanId}
      isFetching={planEntryDetails.isLoading}
      onTryAgainButtonClick={loadDetails}
    />
  );

  const LoaderContent = <RouteDetailsLoaderComponent />;

  return (
    <div className="route_details">
      {planEntryDetails.isError
        ? ErrorContent
        : planEntryDetails.isLoading
          ? LoaderContent
          : DetailsContent}
    </div>
  );
};

export default RouteDetailsComponent;
